/**
 * Hook for fetching grounding images for a session.
 *
 * Calls the tiered cascade endpoint to get available grounding images
 * (admin-uploaded, API-fetched, or empty for text-to-image fallback).
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { getApiClient } from '@/services/api'
import type { GroundingImageOption, GroundingImageSetResponse } from '@/services/api'

export interface UseGroundingImagesResult {
  images: GroundingImageOption[]
  tier: 1 | 2 | 3
  location: string
  isLoading: boolean
  error: string | null
  refresh: () => void
}

export function useGroundingImages(
  sessionId: string | null
): UseGroundingImagesResult {
  const [images, setImages] = useState<GroundingImageOption[]>([])
  const [tier, setTier] = useState<1 | 2 | 3>(3)
  const [location, setLocation] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchImages = useCallback(async () => {
    if (!sessionId) return

    // Abort any in-flight request before starting a new one
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsLoading(true)
    setError(null)

    try {
      const client = getApiClient()
      const result: GroundingImageSetResponse =
        await client.getGroundingImages(sessionId, controller.signal)

      // Guard against state updates after abort/unmount
      if (controller.signal.aborted) return

      setImages(result.images)
      setTier(result.tier)
      setLocation(result.location)
      if (result.error) {
        setError(result.error)
      }
    } catch (err) {
      // Silently ignore aborted requests (component unmounted or new request started)
      if (controller.signal.aborted) return

      console.error('Failed to fetch grounding images:', err)
      setError('Could not load location images')
      // Fall back to tier 3 (text-to-image)
      setImages([])
      setTier(3)
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [sessionId])

  useEffect(() => {
    void fetchImages()

    return () => {
      // Abort in-flight request on unmount or dependency change
      abortControllerRef.current?.abort()
    }
  }, [fetchImages])

  const refresh = useCallback(() => { void fetchImages() }, [fetchImages])

  return { images, tier, location, isLoading, error, refresh }
}
